import { useState, useContext, useRef } from 'react'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { Virtuoso, VirtuosoHandle, FlatIndexLocationWithAlign } from 'react-virtuoso'
import { useFormContext } from 'react-hook-form'

import { createUniqueDeepClone } from '../../../../utils'
import { SubEvents } from '../../../../types'
import type { ModifySubEventsSchema } from '../../../events/Events.schema'
import SubEventForm from './components/SubEventForm'
import { computeSessionId, defaultSubEvent } from './components/SubEvents.utils'
import './components/SessionForm'
import SessionForm from './components/SessionForm'
import { SubEventsStep2Context } from './SubEventsStep2.Context'

const Span = styled('span')<{ lineHeight?: number; bold?: boolean }>(
  ({ theme, lineHeight, bold }) => ({
    color: theme.palette.text.secondary,
    lineHeight: lineHeight ?? undefined,
    fontWeight: bold ? 'bold' : 'normal',
  })
)

interface InnerProps {
  id: number
  sessionKey: number
  virtuosoRef: React.RefObject<VirtuosoHandle>
  customScrollParent: HTMLFormElement | null
  minDateTime: Date | undefined
  maxDateTime: Date | undefined
  onRemove: () => void
}

function InnerSessionList({
  sessionKey,
  id,
  virtuosoRef,
  customScrollParent,
  minDateTime,
  maxDateTime,
  onRemove,
}: InnerProps) {
  const { t } = useTranslation('events')
  const { eventId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const sessionIdParams = searchParams.get('sessionId')
  const subEventIdParams = searchParams.get('subEventId')
  const [idToScrollTo, setIdToScrollTo] = useState<number | FlatIndexLocationWithAlign | null>(null)

  const {
    subEventsFields,
    eventLocation,
    removeSubEvent,
    appendSubEvent,
    appendSession,
    updateSession,
  } = useContext(SubEventsStep2Context)
  const { watch, setValue, getValues } = useFormContext<ModifySubEventsSchema>()

  const sessionId = watch(`sessions.${sessionKey}.id`)
  const showSubEvents = watch(`sessions.${sessionKey}._showSubEvents`) ?? false

  const subEventsIterator: [number, SubEvents][] = subEventsFields.reduce(
    (acc, [key, subEvent]) => {
      if (sessionKey === -1) {
        if (!subEvent.sessions.length) {
          acc.push([key, subEvent])
        }
      } else if (subEvent.sessions.includes(sessionId)) {
        acc.push([key, subEvent])
      }
      return acc
    },
    [] as [number, SubEvents][]
  )

  return (
    <Stack
      spacing={1}
      paddingX={{ sx: 1, sm: 2, md: 3 }}
      alignItems="center"
      flex="1 0"
      width="100%"
      id={computeSessionId(id)}
    >
      {sessionKey === -1 ? (
        <Stack width="100%">
          <h5>{t('Sans séance')}</h5>
          <Stack
            spacing={1}
            padding={2}
            sx={theme => ({
              backgroundColor: theme.colors.greyBackground,
              borderRadius: '0 0 1rem 1rem',
              borderTop: `0.15rem solid ${theme.colors.secondary}`,
            })}
          />
        </Stack>
      ) : (
        <SessionForm
          location={eventLocation}
          sessionKey={sessionKey}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          onRemove={onRemove}
          onDuplicate={data => {
            const newSession = createUniqueDeepClone(data)
            newSession.sub_events = []
            appendSession(newSession)
            navigate(
              `/admin/events/${eventId}/sessions?viewType=dashboard&sessionId=${newSession.id}`
            )
          }}
        />
      )}

      <Stack
        spacing={1}
        padding={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={theme => ({
          backgroundColor: theme.colors.secondaryBackground,
          borderRadius: '1rem',
        })}
        width={{ xs: '95%', sm: '85%' }}
      >
        <Span bold>{t('Détails des interventions')} :</Span>
        <Span>
          {subEventsIterator.length ?? 0}{' '}
          {showSubEvents ? t('intervention(s) affichée(s)') : t('intervention(s) masquée(s)')}
        </Span>
        <Button
          variant="outlined"
          onClick={() => {
            setValue(`sessions.${sessionKey}._showSubEvents`, !showSubEvents)
          }}
        >
          {showSubEvents ? t('Cacher les interventions') : t('Afficher les interventions')}
        </Button>
      </Stack>
      <Stack spacing={1} alignItems="center" width="100%">
        <Virtuoso
          style={{ width: '100%' }}
          data={subEventsIterator}
          customScrollParent={customScrollParent ?? undefined}
          itemContent={(_index, [key, subEvent]) => (
            <SubEventForm
              key={subEvent.id}
              id={subEvent.id}
              subEventKey={key}
              minDateTime={minDateTime}
              maxDateTime={maxDateTime}
              location={eventLocation}
              onUpdateSession={(reason, details) => {
                if (reason === 'selectOption' && details?.option) {
                  const sessions = getValues('sessions')
                  const sessionKey = sessions.findIndex(session => session.id === details.option.id)
                  const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).concat(
                    details.option.id
                  )
                  setValue(`sessions.${sessionKey}.sub_events`, ids)
                  updateSession(sessionKey, {
                    ...sessions[sessionKey],
                    sub_events: ids,
                  })
                }
                if (reason === 'removeOption' && details?.option) {
                  const sessions = getValues('sessions')
                  const sessionKey = sessions.findIndex(session => session.id === details.option.id)
                  const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).filter(
                    id => id !== details.option.id
                  )
                  setValue(`sessions.${sessionKey}.sub_events`, ids)
                  updateSession(sessionKey, {
                    ...sessions[sessionKey],
                    sub_events: ids,
                  })
                }
              }}
              onRemove={() => {
                removeSubEvent(key)
              }}
              onDuplicate={data => {
                const newSubEvent = createUniqueDeepClone(data)
                newSubEvent.sessions = []
                appendSubEvent(newSubEvent)
                navigate(
                  `/admin/events/${eventId}/sessions/subEvent/${newSubEvent.id}?viewType=dashboard`
                )
              }}
              backgroundColor="secondaryBackground"
              width={{ xs: '95%', sm: '85%' }}
              spacing={1}
              showSubEvents={showSubEvents}
            />
          )}
        />
      </Stack>
      <Button
        onClick={event => {
          event.preventDefault()
          const newSubEvent = createUniqueDeepClone(
            defaultSubEvent(
              eventId === 'new' ? -1 : Number(eventId),
              sessionKey !== -1 ? [sessionId] : []
            )
          )
          appendSubEvent(newSubEvent)

          // Update session with new subEvent added
          if (sessionKey !== -1) {
            setValue(`sessions.${sessionKey}.sub_events`, [
              ...getValues(`sessions.${sessionKey}.sub_events`),
              newSubEvent.id,
            ])
          }

          // Visual feedback
          setValue(`sessions.${sessionKey}._showSubEvents`, true)
          setIdToScrollTo({ index: sessionKey, align: 'end', behavior: 'smooth' })
        }}
        sx={{ width: '85%' }}
      >
        {t('Ajouter une intervention')}
      </Button>
    </Stack>
  )
}

interface Step2ViewListProps {
  customScrollParent: HTMLFormElement | null
}

export default function Step2ViewList({ customScrollParent }: Step2ViewListProps) {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')
  const virtuosoRef = useRef<VirtuosoHandle>(null)

  const { eventStart, eventEnd, sessionsFields, removeSession } = useContext(SubEventsStep2Context)

  // useLayoutEffect(() => {
  //   if (sessionId && virtuosoRef.current) {
  //     // console.info('scroll to', sessionId)
  //     const idToScrollTo = eventDetails.sessions.findIndex(
  //       session => session.id === Number(sessionId)
  //     )
  //     virtuosoRef.current?.scrollToIndex({ index: idToScrollTo, behavior: 'smooth' })
  //   }
  // }, [sessionId, virtuosoRef.current])

  return (
    <Stack spacing={1} alignItems="center">
      <Virtuoso
        ref={virtuosoRef}
        style={{ width: '100%' }}
        data={[...sessionsFields, [-1, { id: -1 }] as [number, { id: number }]]}
        customScrollParent={customScrollParent ?? undefined}
        itemContent={(_index, [key, session]) => (
          <InnerSessionList
            virtuosoRef={virtuosoRef}
            customScrollParent={customScrollParent}
            key={session.id}
            id={session.id}
            sessionKey={key}
            minDateTime={eventStart ? new Date(eventStart) : undefined}
            maxDateTime={eventEnd ? new Date(eventEnd) : undefined}
            onRemove={() => {
              removeSession(key)
            }}
          />
        )}
      />
    </Stack>
  )
}
